/**
 * Identity-photo change requests — a resident asking for the facial photo the readers at the gate
 * match against to be SET (they had none) or REPLACED (troca).
 *
 * Not to be confused with the PROFILE photo (`/user/profile`, keyed by Keycloak account), which a
 * resident changes freely with no approval. This one is per customer, lives in
 * person_identity_photos, and only exists as an approved photo once a síndico says so — the
 * candidate is held in identity_photo_requests until then, so nothing reads it by accident.
 */

import { getApiBaseUrl, fetchWithAuth } from './api';
import { okJson } from './httpError';

const base = () => getApiBaseUrl();

export interface IdentityPhotoRequest {
  id: string;
  customer_id: string;
  /** Whose face it is — not necessarily who asked. */
  person_id: string;
  requested_by: string;
  unit_id?: string;
  photo_url: string;
  original_photo_url?: string;
  /** The live photo this would replace. Absent means the person had none (a first photo). */
  replaces_photo_id?: string;
  approved_photo_id?: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  /** The requester's own note. */
  reason?: string;
  /** The síndico's answer on a rejection. Deliberately a separate field from `reason`. */
  decision_notes?: string;
  decided_by?: string;
  decided_at?: string;
  cancelled_at?: string;
  created_at: string;
  updated_at: string;
  person?: {
    id: string;
    first_name: string;
    last_name: string;
    profile_picture_url?: string;
  };
  unit?: { id: string; number: string; block?: { id: string; name: string } };
}

/**
 * The queue. Defaults to what still needs a decision; pass 'all' or a comma-separated list for
 * the history.
 */
export async function listIdentityPhotoRequests(
  customerId: string,
  status = 'pending',
): Promise<{ items: IdentityPhotoRequest[]; total: number }> {
  const r = await fetchWithAuth(
    `${base()}/api/v1/customers/${customerId}/identity-photo-requests?status=${encodeURIComponent(status)}`,
  );
  return okJson(r, 'Erro ao carregar as solicitações de foto');
}

/**
 * Approving installs the photo as the approved + primary identity photo, retires the one it
 * supersedes, and mirrors the URL onto the condomínio person avatar. `device_sync_required` comes
 * back true because the readers are NOT pushed here — enrolment stays an explicit sync.
 */
export async function approveIdentityPhotoRequest(
  customerId: string,
  requestId: string,
): Promise<{ message?: string; photo_id?: string; device_sync_required?: boolean }> {
  const r = await fetchWithAuth(
    `${base()}/api/v1/customers/${customerId}/identity-photo-requests/${requestId}/approve`,
    { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) },
  );
  return okJson(r, 'Erro ao aprovar a foto');
}

/** Rejecting leaves the live photo exactly as it was. The reason reaches the resident. */
export async function rejectIdentityPhotoRequest(
  customerId: string,
  requestId: string,
  reason?: string,
): Promise<{ message?: string }> {
  const r = await fetchWithAuth(
    `${base()}/api/v1/customers/${customerId}/identity-photo-requests/${requestId}/reject`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason: reason || '' }),
    },
  );
  return okJson(r, 'Erro ao rejeitar a foto');
}
